import axios from 'axios';
import { FIGMA_API_BASE_URL } from '@/services/figma';
import { getOidcAccessToken } from '@/services/auth/firebase/oidc-providers/figma-oidc-provider';
import type { ImageFile } from '@/designer/designer';

// TODO:
// Since we are increasing Oauth scope access, we will eventually will want to reprompt already logged-in users to revalidate.
// This is a related eng question to Figma login
export function getFigmaToken(): string | null {
  // TODO: we should access the token using the AuthContext and not directly.
  // postponed until we settle on a final design for oauth access token management in the app.
  return getOidcAccessToken();
}

interface NodePreviewResponse {
  images: Record<string, string>;
}

// Mirrors AxiosConfiguration but kept separate from the app's main axiosInstance (see axiosConfig.ts)
// because Figma uses its own OAuth token rather than the app's Firebase auth token for now (see above TODO)
// The interceptor reads the token fresh on each request to avoid stale token issues
const figmaAxios = axios.create({
  baseURL: FIGMA_API_BASE_URL,
});

figmaAxios.interceptors.request.use((config) => {
  const token = getFigmaToken();
  if (!token) throw new Error('No Figma access token available. Please connect to Figma first.');
  config.headers.Authorization = `Bearer ${token}`;
  return config;
});
interface NodeResponse {
  node_id: string;
}

interface ImageFill {
  url: string;
  type: 'IMAGE';
  blendMode: 'NORMAL';
  scaleMode: 'FILL';
}

interface NodeRequestBase {
  name?: string;
  width?: number;
  height?: number;
  fills?: ImageFill[];
  text?: {
    content: string;
  };
}

interface CreateNodeRequest extends NodeRequestBase {
  parent_id?: string;
  node_type: 'FRAME' | 'TEXT';
  name: string;
}

type UpdateNodeRequest = NodeRequestBase;
export class FigmaApiClient {
  async fetchFigmaNodePreview(fileKey: string, nodeId: string): Promise<ImageFile | undefined> {
    const { data } = await figmaAxios.get<NodePreviewResponse>(
      `/v1/images/${fileKey}?ids=${nodeId}&format=png&scale=1`,
    );

    const imageUrl = data.images[nodeId];
    if (!imageUrl) return undefined;

    const img = new Image();
    img.src = imageUrl;
    await new Promise((resolve) => {
      img.onload = resolve;
      img.onerror = resolve;
    });

    return {
      url: imageUrl,
      width: img.naturalWidth || 1,
      height: img.naturalHeight || 1,
      thumbnailUrl: imageUrl,
      publicId: undefined,
      id: undefined,
    };
  }

  async createNode(fileKey: string, request: CreateNodeRequest): Promise<string> {
    const { data } = await figmaAxios.post<NodeResponse>(`/v1/files/${fileKey}/nodes`, request);
    return data.node_id;
  }

  async updateNode(fileKey: string, nodeId: string, request: UpdateNodeRequest): Promise<string> {
    const { data } = await figmaAxios.put<NodeResponse>(`/v1/files/${fileKey}/nodes/${nodeId}`, request);
    return data.node_id;
  }

  async createImageLayer(
    fileKey: string,
    nodeId: string,
    inputId: string,
    imageDataUrl: string,
    width: number,
    height: number,
  ): Promise<string> {
    return this.createNode(fileKey, {
      parent_id: nodeId,
      node_type: 'FRAME',
      name: `Weavy Input: ${inputId}`,
      width,
      height,
      fills: [
        {
          url: imageDataUrl,
          type: 'IMAGE',
          blendMode: 'NORMAL',
          scaleMode: 'FILL',
        },
      ],
    });
  }

  async createTextLayer(fileKey: string, nodeId: string, inputId: string, text: string): Promise<string> {
    return this.createNode(fileKey, {
      parent_id: nodeId,
      node_type: 'TEXT',
      name: `Weavy Input: ${inputId}`,
      text: {
        content: text,
      },
    });
  }

  async updateImageLayer(fileKey: string, figmaNodeId: string, imageDataUrl: string): Promise<string> {
    return this.updateNode(fileKey, figmaNodeId, {
      fills: [
        {
          url: imageDataUrl,
          type: 'IMAGE',
          blendMode: 'NORMAL',
          scaleMode: 'FILL',
        },
      ],
    });
  }

  async updateTextLayer(fileKey: string, figmaNodeId: string, text: string): Promise<string> {
    return this.updateNode(fileKey, figmaNodeId, {
      text: {
        content: text,
      },
    });
  }
}

let figmaApiClient: FigmaApiClient | null = null;

export function getOrCreateFigmaApiClient(): FigmaApiClient {
  if (!figmaApiClient) {
    figmaApiClient = new FigmaApiClient();
  }
  return figmaApiClient;
}

export function clearFigmaApiClient() {
  figmaApiClient = null;
}
